import { iterGeometries } from './iterGeometries.js';
import { pointToLatlng } from './latlng.js';
/**
 * Compute the overall bounds of a {@link Geometry}, {@link Feature} or {@link FeatureCollection} as a {@link LatLngBounds}
 * @param objects - the Geojson objects to compute the overall bounds of
 * @returns the overall bounds
 * @see {@link ensureBbox} to fill in the `bbox` property of a Geojson object instead
 * @see {@link boundsFromTwoLatLng} to compute bounds from two latlngs instead
 */
export function computeBounds(objects) {
    const min = [Infinity, Infinity];
    const max = [-Infinity, -Infinity];
    function impl(position) {
        const [x, y] = position;
        const [xMin, yMin] = min;
        const [xMax, yMax] = max;
        if (x < xMin)
            min[0] = x;
        if (y < yMin)
            min[1] = y;
        if (x > xMax)
            max[0] = x;
        if (y > yMax)
            max[1] = y;
    }
    for (const geometry of iterGeometries(objects)) {
        switch (geometry.type) {
            case 'Point':
                impl(geometry.coordinates);
                break;
            case 'MultiPoint':
            case 'LineString':
                for (const position of geometry.coordinates)
                    impl(position);
                break;
            case 'MultiLineString':
            case 'Polygon':
                for (const line of geometry.coordinates)
                    for (const position of line)
                        impl(position);
                break;
            case 'MultiPolygon':
                for (const polygon of geometry.coordinates)
                    for (const line of polygon)
                        for (const position of line)
                            impl(position);
                break;
        }
    }
    return [
        [min[1], min[0]],
        [max[1], max[0]],
    ];
}
/**
 * Compute a {@link LatLngBounds} from the coordinates for the two provided {@link LatLng}, ordering them as needed
 *
 * Simply computes the min and max lat/lng and places them correctly in the object
 */
export function boundsFromTwoLatLng(one, two) {
    const lat = [one[0], two[0]];
    const lng = [one[1], two[1]];
    return [
        [Math.min(...lat), Math.min(...lng)],
        [Math.max(...lat), Math.max(...lng)],
    ];
}
/**
 * Compute a {@link LatLngBounds} from the coordinates for the two provided {@link Position}, {@link Point} or {@link Feature} points, ordering them as needed
 * @see {@link boundsFromTwoLatLng} the function this calls internally
 * @see {@link computeBounds} to compute bounds from an arbitrary list of geometries
 */
export function boundsFromTwoPoints(one, two) {
    return boundsFromTwoLatLng(pointToLatlng(one), pointToLatlng(two));
}
/**
 * Ensure the `bbox` field of a Geojson object is filled in, returning a new object with it if it wasn't
 * @param object - the object to operate on
 * @see {@link computeBounds} the function that actually computes the bounding box of the object
 */
export function ensureBbox(object, { recompute = false, mutate = false, } = {}) {
    if (object.bbox?.length === 4 && !recompute)
        return object;
    const [[latMin, lngMin], [latMax, lngMax]] = computeBounds([object]);
    if (!mutate)
        object = { ...object };
    object.bbox = [lngMin, latMin, lngMax, latMax];
    return object;
}
//# sourceMappingURL=data:application/json;base64,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